/**
 * Async bootstrap shim — keeps `main.tsx` intentionally devoid of
 * static imports so the module-loading race with wallet-extension
 * script injection on `nuradocs.com` doesn't deadlock the entry
 * before the first statement runs. See
 * `docs/SESSION_HANDOFF.md` for the full bisect history.
 *
 * The pattern:
 *   1. No static imports here (besides this comment).
 *   2. Yield one microtask to let TronLink / Phantom / MetaMask-
 *      compatible providers finish their synchronous injection.
 *   3. Dynamically `import('./boot')`, which owns the real module
 *      graph (React, Router, Supabase, …). Because that import is
 *      scheduled as a microtask after the yield, factory invocation
 *      happens past the extensions' injection window and the hang
 *      we observed in Phase-C tests does not reproduce.
 *
 * If the app ever renders on `<id>.nuradocs.pages.dev` but blanks on
 * `nuradocs.com`, check here first — the hang is origin-specific
 * and almost certainly an extension timing issue.
 */

;(async function () {
  // Yield: a `setTimeout(..., 0)` is enough to move past document-
  // start listeners that extensions attach.
  await new Promise((resolve) => setTimeout(resolve, 0));
  try {
    const mod = await import('./boot');
    if (typeof mod.default === 'function') {
      await mod.default();
    }
  } catch (err: any) {
    // Surface a visible error instead of a silent blank, in case
    // the bootstrap path itself breaks on some future regression.
    document.title = '[BOOT-FAIL] ' + (err?.message || String(err)).slice(0, 160);
    // eslint-disable-next-line no-console
    console.error('[main] boot failed:', err);
  }
})();
